<template>
  <!-- Buttons for Refresh and Print -->
  <div class="button-container">
    <button @click="refreshReports">Refresh</button>
    <button @click="printAllReports" :disabled="loading">Print</button>
    <button @click="collapseAllReports">Collapse All</button>
    <button
      :class="{ pressed: searchContent }"
      @click="searchContent = !searchContent"
    >
      Search Content
    </button>
    <input
      type="text"
      v-model="searchQuery"
      placeholder="Search reports..."
      class="search-input"
    />
    <button
      style="margin-left: -10px"
      @click="
        searchQuery = '';
        filterQuery = '';
      "
    >
      X
    </button>
    <div class="report-stats-wrapper">
      <span v-if="reportStats" class="report-stats">
        from: {{ formatDate(reportStats.oldestDate) }} to:
        {{ formatDate(reportStats.newestDate) }} total records:
        {{ reportStats.totalRecords }}
      </span>
    </div>
  </div>

  <!-- Loading overlay for print operation -->
  <div v-if="loadingOverlay" class="overlay">Generating PDF...</div>

  <div ref="reportViewer" class="report-viewer">
    <div v-if="loading" class="loading-message">Reports Generating...</div>
    <h1 v-if="!loading">All Reports</h1>
    <div class="report-content">
      <div
        v-for="report in filteredReports"
        :key="report.id"
        class="report-container"
        :data-report-id="report.id"
      >
        <!-- Toggle visibility on title click -->
        <h2 class="report-title" @click="toggleReportVisibility(report.id)">
          <!-- Title with highlighted search matches -->
          <span v-html="highlightText(report.title)"></span>

          <!-- Down arrow icon when report is collapsed -->
          <span v-if="!report.isVisible" class="down-arrow">▼</span>

          <!-- Share icon -->
          <span
            class="share-icon fas fa-share-alt"
            @click.stop="shareReport(report)"
            title="Copy link to this report"
          ></span>

          <!-- Print icon on the right side -->
          <span
            class="print-icon fas fa-print"
            @click.stop="printSingleReport(report)"
          ></span>
        </h2>

        <!-- Conditionally display content based on visibility state -->
        <div
          v-show="report.isVisible"
          v-html="highlightText(report.content)"
          class="report-table"
        ></div>

        <!-- Footnotes section, shown only if report is visible -->
        <div
          v-if="report.isVisible"
          class="report-footnote-container"
          @dblclick="openEditModal(report)"
          :title="'Double-click to edit footnote'"
        >
          <strong class="footnote-label">Footnotes</strong>
          <div
            class="footnote-content"
            v-html="report.renderedFootnote || ''"
          ></div>
        </div>
      </div>
    </div>
  </div>

  <!-- Lease Search iframe overlay -->
  <div
    v-if="iframeUrl"
    class="report-iframe-overlay"
    @click.self="iframeUrl = null"
  >
    <div class="report-iframe-container">
      <div class="report-iframe-title-bar">
        <span>{{ iframeTitle }}</span>
        <button class="report-iframe-close-btn" @click="iframeUrl = null">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="report-iframe-scroll-wrapper">
        <div v-if="iframeLoading" class="report-iframe-spinner-overlay">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Loading lease…</span>
        </div>
        <iframe
          :src="iframeUrl"
          class="report-lease-iframe"
          @load="iframeLoading = false"
        ></iframe>
      </div>
    </div>
  </div>

  <!-- Modal for editing the footnote -->
  <div v-if="showModal" class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h3>Edit Footnote</h3>
      </div>
      <textarea
        ref="footNoteInput"
        v-model="editableFootnote"
        class="modal-textarea"
        placeholder="Enter footnote..."
      ></textarea>
      <div class="modal-actions">
        <button @click="saveFootnote">Save</button>
        <button @click="closeModal">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import { marked } from 'marked';
import debounce from 'lodash/debounce';

export default {
  name: 'ReportViewer',
  data() {
    return {
      loading: true,
      reportHtmlList: [],
      showModal: false,
      editableFootnote: '',
      currentReport: null,
      loadingOverlay: false, // Overlay for PDF generation
      searchQuery: '', // Input binding - updates immediately
      filterQuery: '', // Actual filter value - updates after debounce
      searchContent: false,
      debouncedFilterReports: null, // Placeholder for debounced function
      reportStats: {
        oldestDate: null,
        newestDate: null,
        totalRecords: 0,
        recordsByYear: [],
      },
      iframeUrl: null,
      iframeTitle: '',
      iframeLoading: false,
    };
  },

  computed: {
    // Computed property to filter reports based on the filter query (debounced)
    filteredReports() {
      const query = this.filterQuery.toLowerCase();
      return this.reportHtmlList.filter(
        (report) =>
          report.title.toLowerCase().includes(query) ||
          (this.searchContent && report.content.toLowerCase().includes(query)),
      );
    },
  },

  async mounted() {
    document.title = 'Reports';

    // Check for URL query parameter to filter to a specific report
    const urlParams = new URLSearchParams(window.location.search);
    const queryFilter = urlParams.get('q');

    if (queryFilter) {
      // Pre-populate search and load only matching report
      this.searchQuery = queryFilter;
      this.filterQuery = queryFilter;
      await this.loadReports(queryFilter);
    } else {
      await this.loadReports();
    }

    // Add double-click handler for clickable address cells
    this.$refs.reportViewer.addEventListener(
      'dblclick',
      this.handleAddressDoubleClick,
    );

    axios
      .get('/api/report-stats')
      .then((response) => {
        this.reportStats = response.data;
      })
      .catch((error) => {
        console.error('Error fetching report stats:', error);
        this.error = 'Failed to load report statistics.';
      })
      .finally(() => {
        this.loading = false;
      });
  },

  beforeUnmount() {
    // Clean up event listener
    if (this.$refs.reportViewer) {
      this.$refs.reportViewer.removeEventListener(
        'dblclick',
        this.handleAddressDoubleClick,
      );
    }
  },

  methods: {
    handleAddressDoubleClick(event) {
      const target = event.target.closest('.clickable-address');
      if (target && target.dataset.address) {
        const address = target.dataset.address.trim();
        if (address && address !== 'GRAND TOTAL') {
          this.iframeTitle = address;
          this.iframeLoading = true;
          this.iframeUrl = `/lease-search?search=${encodeURIComponent(address)}&hideNav=1`;
        }
      }
    },

    formatDate(theDate) {
      if (!theDate) return '';
      const localDate = new Date(theDate);
      const month = String(localDate.getMonth() + 1).padStart(2, '0'); // Months are zero-based
      const day = String(localDate.getDate()).padStart(2, '0');
      const year = localDate.getFullYear();

      return `${month}/${day}/${year}`;
    },

    collapseAllReports() {
      this.reportHtmlList.forEach((report) => {
        report.isVisible = false;
      });
    },

    highlightText(text) {
      if (!this.filterQuery) return text;

      // Strip out HTML tags to only work with the inner text content
      const plainText = text.replace(/<\/?[^>]+(>|$)/g, '');

      // Escape special characters in the search query
      const query = this.filterQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${query})`, 'gi');

      // Highlight the matches within plain text
      plainText.replace(regex, '<span class="highlight">$1</span>');

      // Reinsert the HTML structure around the highlighted plain text
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;

      // Replace only the text nodes with highlighted text
      function replaceTextNodes(node) {
        node.childNodes.forEach((child) => {
          if (child.nodeType === Node.TEXT_NODE) {
            const textWithHighlights = child.textContent.replace(
              regex,
              '<span class="highlight">$1</span>',
            );
            const newNode = document.createElement('span');
            newNode.innerHTML = textWithHighlights;
            node.replaceChild(newNode, child);
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            replaceTextNodes(child); // Recurse through child elements
          }
        });
      }
      replaceTextNodes(tempDiv);

      return tempDiv.innerHTML;
    },

    async loadReports(filterTitle = null) {
      this.loading = true;
      try {
        const response = await axios.get('/api/all-reports');
        let reportsToLoad = response.data;

        // If a filter is provided, only load matching reports
        if (filterTitle) {
          const filterLower = filterTitle.toLowerCase();
          reportsToLoad = reportsToLoad.filter((report) =>
            (report.reportTitle || '').toLowerCase().includes(filterLower),
          );
        }

        this.reportHtmlList = await Promise.all(
          reportsToLoad.map(async (report) => {
            const reportContent = await this.fetchReport(report.id);
            // When filtering by URL param, expand matching reports; otherwise collapse all
            const isVisible = filterTitle ? true : false;

            return {
              id: report.id,
              title: report.reportTitle || 'Untitled Report',
              content: reportContent,
              footnote: report.footnote,
              renderedFootnote: marked(report.footnote || ''),
              isVisible,
            };
          }),
        );
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        this.loading = false;
      }
    },

    shareReport(report) {
      // Create a URL with the report title as query parameter
      const baseUrl = window.location.origin + '/reports';
      const shareUrl = `${baseUrl}?q=${encodeURIComponent(report.title)}`;

      // Show popup with selectable text
      const popupHtml = `
        <div style="background: white; padding: 20px; border-radius: 8px; max-width: 500px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
            <h3 style="margin: 0; color: #333;">Share Report Link</h3>
            <button class="close-popup-btn" style="background: none; border: none; font-size: 24px; cursor: pointer; color: #999;">&times;</button>
          </div>
          <textarea readonly class="share-url-textarea" style="width: 92%; min-height: 60px; padding: 10px; font-family: monospace; font-size: 13px; border: 1px solid #ddd; border-radius: 4px; resize: none; cursor: pointer;" title="Click to select all">${shareUrl}</textarea>
          <button class="copy-link-btn" style="margin-top: 10px; padding: 10px 20px; background: #955c99; color: white; border: none; border-radius: 4px; cursor: pointer; width: 100%;">Copy to Clipboard</button>
        </div>
      `;

      // Create modal overlay
      const overlay = document.createElement('div');
      overlay.style.cssText =
        'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 999999;';
      overlay.innerHTML = popupHtml;

      document.body.appendChild(overlay);

      // Textarea click to select
      const textarea = overlay.querySelector('.share-url-textarea');
      textarea.addEventListener('click', function () {
        this.select();
      });

      // Close button
      const closeBtn = overlay.querySelector('.close-popup-btn');
      closeBtn.addEventListener('click', () => {
        overlay.remove();
      });

      // Copy button
      const copyBtn = overlay.querySelector('.copy-link-btn');
      copyBtn.addEventListener('click', () => {
        textarea.select();
        textarea.setSelectionRange(0, 99999);

        try {
          const successful = document.execCommand('copy');
          if (successful) {
            copyBtn.textContent = 'Copied!';
            copyBtn.style.background = '#4CAF50';
            setTimeout(() => overlay.remove(), 800);
          } else {
            copyBtn.textContent = 'Failed - try manual copy';
            copyBtn.style.background = '#f44336';
          }
        } catch (err) {
          console.error('Copy failed:', err);
          copyBtn.textContent = 'Failed - try manual copy';
          copyBtn.style.background = '#f44336';
        }
      });

      // Click outside to close
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          overlay.remove();
        }
      });
    },

    async fetchReport(id) {
      try {
        const response = await axios.get(`/api/report/${id}`);
        return response.data;
      } catch (error) {
        console.error(`Error fetching report ${id}:`, error);
        return `<p>Error loading report ${id}</p>`;
      }
    },

    toggleReportVisibility(reportId) {
      const report = this.reportHtmlList.find((r) => r.id === reportId);
      if (report) {
        report.isVisible = !report.isVisible;
      }
    },

    openEditModal(report) {
      this.currentReport = report;
      this.editableFootnote = report.footnote || '';
      this.showModal = true;

      this.$nextTick(() => {
        if (this.$refs.footNoteInput) this.$refs.footNoteInput.focus();
      });
    },

    closeModal() {
      this.showModal = false;
    },

    async saveFootnote() {
      if (!this.currentReport) return;

      try {
        await axios.post('/api/save-report-footnote', {
          id: this.currentReport.id,
          footnote: this.editableFootnote,
        });

        this.currentReport.footnote = this.editableFootnote;
        this.currentReport.renderedFootnote = marked(
          this.editableFootnote || '',
        );
        this.showModal = false;
      } catch (error) {
        console.error('Error saving footnote:', error);
        alert('Failed to save footnote. Please try again.');
      }
    },

    async refreshReports() {
      await this.loadReports();
    },

    inlineScopedCSS(element) {
      // Inject scoped styles for report printing
      return (
        `
    <style>
      /* Body and general styles */
      body {
        padding: 20px;
        font-family: Arial, sans-serif;
        font-size: 14px;
        color: #333;
      }

      /* Report container */
      .report-container {
        margin-bottom: 30px;
      }

      /* Report title */
      .report-title {
        font-size: 1.5em;
        font-weight: bold;
        margin-bottom: 10px;
      }

      /* Table styling */
      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 10px;
        font-size: 0.9em;
        padding-bottom: 15px;
      }

      /* Header styling */
      th {
        background-color: #ccc;
        text-align: left;
        padding: 8px;
        border: 1px solid #aaa;
        font-weight: bold;
      }

      /* Zebra striping for rows */
      tbody tr:nth-child(even) {
        background-color: #f2f2f2;
      }
      tbody tr:nth-child(odd) {
        background-color: none;
      }

      /* Table cell styling */
      td {
        padding: 8px;
        border: 1px solid #ddd;
        vertical-align: top;
      }

      tfoot {
        border: none !important; /* This will override any border settings */
      }

      /* If the border is on the cells within the footer */

      tfoot td, tfoot th {
        border: none !important;
      }

      /* Alignment classes */
      .left-align { text-align: left; }
      .right-align { text-align: right; }
      .center-align { text-align: center; }

      /* Footnote styling */
      .report-footnote {
        font-style: italic;
        color: #666;
        margin-top: 15px;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
      }

      /* Footer */
      .footer {
        width: 100%;
        text-align: center;
        font-size: 0.8em;
        color: #666;
        background: #fff;
        padding-top: 10px;
        margin-top: 30px;
        height:40px;
        margin-top:10px;
        z-index:99;
      }
        .footer-space {
        height: 20px}

    </style>
    ` + element
      );
    },

    // Helper function to apply inline CSS and print all reports
    async printAllReports() {
      this.loadingOverlay = true; // Show overlay while processing

      // Make all reports visible before capturing HTML content
      // this.reportHtmlList.forEach((report) => (report.isVisible = true));
      // await this.$nextTick();

      // Capture the HTML content of the reports
      const element = this.$refs.reportViewer;
      if (!element) {
        console.error('Report viewer element not found');
        return;
      }

      // Apply inline scoped CSS to the HTML content
      const htmlContentWithCSS = this.inlineScopedCSS(element.innerHTML);

      try {
        // Send HTML with inline CSS to backend for PDF generation
        const response = await axios.post(
          '/api/convert-to-pdf',
          { html: htmlContentWithCSS },
          { responseType: 'blob' },
        );

        // Create a downloadable PDF file from the response
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = 'All_Reports.pdf';
        link.click();

        // Clean up URL object
        URL.revokeObjectURL(pdfUrl);
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('Failed to generate PDF. Please try again.');
      } finally {
        this.loadingOverlay = false; // Hide overlay
      }
    },

    printSingleReport(report) {
      // Ensure the report is visible before printing
      report.isVisible = true;

      this.$nextTick(() => {
        const reportContainer = this.$refs.reportViewer.querySelector(
          `.report-container[data-report-id="${report.id}"]`,
        ).innerHTML;

        const printDate = new Date().toLocaleString(); // Current date and time

        // Create a hidden iframe for printing
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`
        <html>
          <head>
            <title>Print Report - ${report.title}</title>
            <style>
              @media print {
                @page {
                  size: landscape;
                  margin: 1in;
                }
                body { font-family: Arial, sans-serif}
                .report-title { font-size: 1.5em; margin-bottom: 10px; }
                .report-footnote { font-style: italic; margin-top: 10px; }

                /* Zebra striping for table rows */
                .report-table tr:nth-child(even) {
                  background-color: #ddd;
                }
                .report-table tr:nth-child(odd) {
                  background-color: white;
                }

                /* Footer style for date and time of printing */
                .print-footer {
                  position: fixed;
                  bottom: 0;
                  left: 0;
                  width: 100%;
                  text-align: center;
                  font-size: 0.8em;
                  color: #666;
                  padding-top: 10px;
                  margin-top:10px;
                  background: #fff;
                }
              }
              tfoot {
                border: none !important; /* This will override any border settings */
              }

              /* If the border is on the cells within the footer */

              tfoot td, tfoot th {
                border: none !important;
              }
            </style>
          </head>
          <body>
            ${reportContainer}
            <!-- Footer for the print date and time -->
            <div style="margin-top:-10px; height:20px; " class="print-footer">
              Printed on ${printDate}
            </div>
          </body>
        </html>
      `);
        doc.close();

        // Print the content of the iframe
        iframe.contentWindow.print();

        // Remove the iframe after printing, with a delay to allow for printing
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000); // Adjust the delay if necessary
      });
    },
  },

  watch: {
    searchQuery() {
      this.debouncedFilterReports();
    },
  },

  created() {
    // Initialize the debounced function
    this.debouncedFilterReports = debounce(() => {
      // Update filterQuery which triggers the computed property
      this.filterQuery = this.searchQuery;
    }, 600); // Delay in milliseconds before filtering
  },
};
</script>

<style scoped>
.button-container button {
  padding: 8px 12px;
  margin-right: 8px;
  font-size: 0.9em;
  cursor: pointer;
  border-radius: 4px;
  border: 1pt solid #ddd;
  width: 130px;
}

.report-viewer {
  height: 85vh;
  width: 1200px;
  padding: 10px;
  border: 1px solid #ddd;
  background-color: #fff;
  margin-left: 40px;
  margin-top: 10px;
  overflow-y: auto;
  font-size: 0.75em;
  font-family: Arial, Helvetica, sans-serif;
}

.loading-message {
  font-size: 2em;
  font-weight: bold;
  color: #333;
  text-align: center;
  padding-top: 5vh;
  padding-bottom: 5vh;
}

.report-title {
  font-size: 1.5em;
  margin-top: 20px;
  cursor: pointer;
  color: #333;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.down-arrow {
  font-size: 0.8em;
  margin-left: 5px;
}

.print-icon {
  font-size: 1em;
  margin-left: 10px;
  cursor: pointer;
  color: #ddd;
}

.share-icon {
  font-size: 1em;
  margin-left: auto;
  cursor: pointer;
  color: #ddd;
}

.share-icon:hover,
.print-icon:hover {
  color: #a080a8;
}

.report-footnote {
  margin-top: 4px;
  font-style: italic;
  color: #666;
  padding: 10px;
  border: 1px solid #ddd;
  min-height: 30px;
  cursor: pointer;
  user-select: none;
}

/* Modal overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal {
  user-select: none;
  background: #fff;
  width: 400px;
  height: 300px;
  border-radius: 4px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.modal-header {
  background-color: #333;
  color: #ddd;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  text-align: center;
  font-size: 1.2em;
}

.modal-header h3 {
  margin-top: 4px;
  margin-bottom: 4px;
}

.modal-textarea {
  width: 90%;
  height: 90%;
  margin: 10px auto;
  padding: 5px;
  font-size: 1em;
}

.modal-actions {
  display: flex;
  justify-content: space-around;
  padding: 10px;
}

.modal-actions button {
  padding: 5px 10px;
  font-size: 0.85em;
  cursor: pointer;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
  height: 35px;
  width: 75px;
  background-color: #955c99;
  color: #fff;
  border: none;
  border-radius: 3px;
}

.button-container {
  display: flex;
  gap: 10px;
  position: relative;
  left: 40px; /* Fix the button container 85px from the left */
}

.report-content {
  max-width: 1200px;
  padding-top: 0px;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5em;
  z-index: 1000;
}

.pressed {
  background-color: #ccc; /* Darker background to indicate active state */
  border: 1px solid #999;
  color: #333;
  box-shadow: inset 2px 2px 5px rgba(0, 0, 0, 0.2);
}

:deep(.highlight) {
  background-color: rgb(192, 142, 196) !important; /* Highlight color */
  color: black;
  font-weight: bold;
}

.report-footnote-container {
  display: flex;
  align-items: flex-start;
  gap: 10px; /* Space between label and content */
  margin-top: 15px;
  padding: 10px;
  border: 1px solid #ddd;
}

.footnote-label {
  font-weight: bold;
  font-style: italic;
  color: #666;
  min-width: 80px; /* Adjust width as needed to align with other elements */
}

.footnote-content {
  flex: 1;
  color: #333;
}
.footnote-content :deep(p) {
  margin-top: 0 !important;
}
.report-stats {
  margin-left: -242px;
  font-weight: bold;
  color: #555;
}

.report-stats-wrapper {
  position: relative;
  right: -231px;
  top: 12px;
  text-align: right;
  font-weight: normal;
  font-size: 0.8em;
}

/* Clickable address cells in reports */
:deep(.clickable-address) {
  cursor: pointer;
}

:deep(.clickable-address:hover) {
  text-decoration: underline;
  color: #a080a8;
}

/* Highlight table row on hover */
:deep(.report-table tbody tr:hover) {
  background-color: #e8d8eb !important;
}

/* Lease Search iframe overlay */
.report-iframe-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.55);
  z-index: 30000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.report-iframe-container {
  width: 90vw;
  max-width: 1400px;
  height: 100vh;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
}

.report-iframe-title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  background: #333;
  color: #eee;
  font-size: 0.95em;
  flex-shrink: 0;
}

.report-iframe-close-btn {
  background: #555;
  border: 1px solid #777;
  color: #eee;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
}

.report-iframe-close-btn:hover {
  background: #d32f2f;
}

.report-iframe-scroll-wrapper {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.report-iframe-spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #1e1e2e;
  z-index: 1;
  gap: 12px;
  color: #ccc;
  font-size: 1.1em;
}

.report-iframe-spinner-overlay .fa-spinner {
  font-size: 2em;
  color: #4fc3f7;
}

.report-lease-iframe {
  border: none;
  width: 100%;
  height: 100%;
}
</style>
